import * as React from 'react';

import {
  DimeDropdownMenuLabel,
  DimeDropdownMenuPortal,
  DimeDropdownMenuSubContent,
} from '../DimeDropdownMenu.js';
import { DimeInput } from '../DimeInput.js';
import { cn } from '../lib/utils.js';
import type {
  DimeActionToolbarMenuItem,
  DimeActionToolbarMenuSearch,
} from './DimeActionToolbar.types.js';
import { DimeActionToolbarMenuContent } from './DimeActionToolbarMenuContent.js';
import { DimeActionToolbarMenuItems } from './DimeActionToolbarMenuItems.js';

/** Short menus stay a plain list; the search box only earns its space past this many items. */
const SEARCH_MIN_ITEMS = 8;

type DimeActionToolbarMenuBodyProps = Readonly<{
  items: DimeActionToolbarMenuItem[];
  label?: string;
  search?: DimeActionToolbarMenuSearch;
  align?: React.ComponentProps<typeof DimeActionToolbarMenuContent>['align'];
  className?: string;
  /** Renders as a submenu's popup, under a `DimeDropdownMenuSub`. */
  sub?: boolean;
}>;

type ContainerProps = Readonly<{
  sub: boolean;
  align?: DimeActionToolbarMenuBodyProps['align'];
  className?: string;
  role?: string;
  'aria-orientation'?: undefined;
  onOpenAutoFocus?: (event: Event) => void;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  children: React.ReactNode;
}>;

function MenuBodyContainer({ sub, align, className, children, ...props }: ContainerProps) {
  const width = cn('min-w-44', className);
  return sub ? (
    // Portaled so a searchable submenu's dialog popup sits outside the parent menu, which may only own menu items.
    <DimeDropdownMenuPortal>
      <DimeDropdownMenuSubContent className={width} {...props}>
        {children}
      </DimeDropdownMenuSubContent>
    </DimeDropdownMenuPortal>
  ) : (
    <DimeActionToolbarMenuContent align={align} className={width} {...props}>
      {children}
    </DimeActionToolbarMenuContent>
  );
}

export function DimeActionToolbarMenuBody({
  items,
  label,
  search,
  align,
  className,
  sub = false,
}: DimeActionToolbarMenuBodyProps) {
  const [query, setQuery] = React.useState('');
  const labelId = React.useId();
  const listRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const searchable = search !== undefined && items.length >= SEARCH_MIN_ITEMS;
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? items.filter((item) =>
        [item.label, item.description ?? ''].some((text) => text.toLowerCase().includes(needle)),
      )
    : items;
  const heading = label ? (
    <DimeDropdownMenuLabel id={labelId} className="shrink-0">
      {label}
    </DimeDropdownMenuLabel>
  ) : null;

  if (!searchable) {
    return (
      <MenuBodyContainer sub={sub} align={align} className={className}>
        {heading}
        <DimeActionToolbarMenuItems items={items} />
      </MenuBodyContainer>
    );
  }

  return (
    // A menu may only own menu items, so a searchable menu's popup is a dialog holding the search box beside the menu.
    <MenuBodyContainer
      sub={sub}
      align={align}
      // Only the list scrolls, so arrowing down a long list never carries the search box out of view.
      className={cn('flex flex-col overflow-y-hidden', className)}
      role="dialog"
      aria-orientation={undefined}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        searchRef.current?.focus();
      }}
      // Focus arriving from outside is a keyboard-opened submenu, whose first-item focus Radix applies after this, so the search box takes it a frame later.
      onFocus={(event) => {
        const popup = event.currentTarget;
        const fromOutside = !popup.contains(event.relatedTarget as Node | null);
        if (event.target !== popup || !fromOutside) return;
        requestAnimationFrame(() => {
          if (popup.contains(document.activeElement)) searchRef.current?.focus();
        });
      }}
      onKeyDown={(event) => {
        const firstItem = listRef.current?.querySelector('[role="menuitem"]');
        if (event.key === 'ArrowUp' && event.target === firstItem) {
          event.preventDefault();
          searchRef.current?.focus();
        }
      }}
    >
      {heading}
      <DimeInput
        ref={searchRef}
        type="search"
        aria-label={search.placeholder}
        placeholder={search.placeholder}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          // Only navigation keys and Escape may reach the menu, whose typeahead would otherwise swallow each key typed into the box.
          if (event.key !== 'ArrowDown' && event.key !== 'Escape' && event.key !== 'Tab')
            event.stopPropagation();
          if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
            event.preventDefault();
            listRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
          }
        }}
        className="my-1 h-8 shrink-0"
      />
      {visible.length > 0 ? (
        <div
          ref={listRef}
          role="menu"
          className="min-h-0 overflow-y-auto"
          aria-orientation="vertical"
          aria-labelledby={label ? labelId : undefined}
        >
          <DimeActionToolbarMenuItems items={visible} />
        </div>
      ) : null}
      <div role="status" aria-live="polite">
        {visible.length === 0 ? (
          <DimeDropdownMenuLabel className="font-normal text-muted-foreground">
            No matches
          </DimeDropdownMenuLabel>
        ) : null}
      </div>
    </MenuBodyContainer>
  );
}
